import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import {
  ClipboardList,
  Plus,
  Search,
  Filter,
  Pencil,
  Building2,
} from "lucide-react";

const API = "https://nissi-india.com/api";

const PurchaseStoreMaterialListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  console.log("=== SESSION DATA ===");
  console.log("finYearId:", finYearId);
  console.log("finYear:", finYear);
  console.log("branchId:", branchId);
  console.log("branch:", branch);
  console.log("userId:", userId);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const loginuserid = Number(userId) || 0;
  const fld_branchid = Number(branchId) || 0;

  const normalizedBranch = String(branch || "").trim().toUpperCase();
  const isHO =
    normalizedBranch === "HO" ||
    normalizedBranch === "HEAD-OFFICE" ||
    normalizedBranch === "HEAD OFFICE";

  console.log("isHO:", isHO, "| fld_branchid:", fld_branchid);

  const [location, setLocation] = useState(isHO ? "HO" : "BRANCH");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [reqNo, setReqNo] = useState("");
  const [projectCode, setProjectCode] = useState("");
  const [projectName, setProjectName] = useState("");
  const [store, setStore] = useState("");
  const [status, setStatus] = useState("Waiting");
  const [storeOptions, setStoreOptions] = useState([]);
  const [results, setResults] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [branchType, setBranchType] = useState(isHO ? "HO" : "Branch");
  const [loading, setLoading] = useState(false);

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  useEffect(() => {
    if (isHO) {
      setBranchType("HO");
      setLocation("HO");
    } else {
      setBranchType("Branch");
      setLocation("BRANCH");
    }
  }, [isHO]);

  // ── Fetch Store Dropdown ─────────────────────────────────────
  useEffect(() => {
    const fetchStoreOptions = async () => {
      if (!fld_branchid) {
        console.warn("No branchId – skipping store fetch");
        return;
      }

      const payload = {
        branchtype: isHO ? "HO" : "BRANCH",
        branchid: fld_branchid,
      };

      console.log("=== FETCH STORE OPTIONS ===");
      console.log("Payload:", payload);
      console.log("Headers:", commonHeaders);
      console.log("URL:", `${API}/getFromStoreOptions`);

      try {
        const res = await axios.post(`${API}/getFromStoreOptions`, payload, {
          headers: commonHeaders,
        });

        console.log("Store API Response:", res.data);

        const options = res.data?.storeOptions || res.data?.data || res.data || [];
        console.log("Parsed store options:", options);

        setStoreOptions(Array.isArray(options) ? options : []);
      } catch (error) {
        console.error("=== STORE DROPDOWN ERROR ===");
        console.error("Error object:", error);
        console.error("Response:", error.response?.data);
        console.error("Status:", error.response?.status);
        console.error("Message:", error.message);
        toast.error("Failed to load store options");
      }
    };

    fetchStoreOptions();
  }, [isHO, fld_branchid, commonHeaders]);

  // ── Search ───────────────────────────────────────────────────
  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select From Date and To Date");
      return;
    }

    const params = {
      branchtype: location,
      fld_type: location === "HO" ? "H" : "B",
      branchid: fld_branchid,
      reqno: reqNo || "",
      fromdate: fromDate,
      todate: toDate,
      fromstore: store ? Number(store) : "",
      projectid: projectCode || "",
      status,
      loginuserid,
    };

    console.log("=== SEARCH PARAMS ===");
    console.log(params);

    try {
      setLoading(true);
      const res = await axios.get(`${API}/getPurchaseStoreMaterialReqList`, {
        params,
        headers: commonHeaders,
      });

      console.log("Search API Response:", res.data);

      const rows = res.data?.data || res.data || [];
      setResults(Array.isArray(rows) ? rows : []);
      toast.success(`Loaded ${rows.length} records`);
    } catch (err) {
      console.error("=== SEARCH ERROR ===");
      console.error(err);
      console.error("Response:", err.response?.data);
      toast.error("Failed to fetch listing");
    } finally {
      setLoading(false);
    }
  };

  const handleBranchTypeChange = (value) => {
    console.log("Branch type changed to:", value);
    setBranchType(value);
    setLocation(value === "HO" ? "HO" : "BRANCH");
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate(new Date().toISOString().split("T")[0]);
    setReqNo("");
    setProjectCode("");
    setProjectName("");
    setStore("");
    setStatus("Waiting");
  };

  const handleEdit = (row) => {
    const reqId = row?.fld_PurchaseRequestid;
    console.log("Edit clicked, reqId:", reqId, "row:", row);
    if (!reqId) {
      toast.error("Request ID not found for this row");
      return;
    }
    navigate(`/inventory/Purchase_Store_Material_Req?reqId=${reqId}`);
  };

  const getStatusBadge = (statusValue) => {
    const s = statusValue || status;
    if (s === "Approved") return "bg-green-100 text-green-800";
    if (s === "Waiting") return "bg-yellow-100 text-yellow-800";
    if (s === "Cancel") return "bg-red-100 text-red-800";
    if (s === "DisApproved") return "bg-orange-100 text-orange-800";
    if (s === "Close") return "bg-gray-100 text-gray-800";
    return "bg-gray-100 text-gray-800";
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Purchase / Material Requisition
          </span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <ClipboardList className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Material Requisition Records</h1>
                <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search and manage purchase material requisitions
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                }`}
              >
                {results.length} records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                } ${showFilters ? (isDark ? "bg-slate-800" : "bg-slate-100") : ""}`}
              >
                <Filter size={16} />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate("/inventory/Purchase_Store_Material_Req")}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
              >
                <Plus size={16} />
                Add New
              </button>
            </div>
          </div>

          {/* Branch Type Toggle */}
          <div className="mb-2">
            <label className={labelClass}>Branch Type</label>
            <div className="flex gap-2">
              <button
                onClick={() => handleBranchTypeChange("HO")}
                disabled={!isHO}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                  branchType === "HO"
                    ? `${buttonClass} ${hoverClass} text-white shadow`
                    : isDark
                    ? "bg-slate-800 text-slate-300 border border-slate-600"
                    : "bg-gray-100 text-gray-700 border border-gray-200"
                } ${!isHO ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <Building2 size={16} />
                Head Office
              </button>

              <button
                onClick={() => handleBranchTypeChange("Branch")}
                disabled={isHO}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                  branchType === "Branch"
                    ? "bg-blue-600 text-white shadow"
                    : isDark
                    ? "bg-slate-800 text-slate-300 border border-slate-600"
                    : "bg-gray-100 text-gray-700 border border-gray-200"
                } ${isHO ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Branch
              </button>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        {showFilters && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-lg font-bold">Search Filters</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div>
                  <label className={labelClass}>
                    From Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Req No</label>
                  <input
                    type="text"
                    value={reqNo}
                    onChange={(e) => setReqNo(e.target.value)}
                    placeholder="Enter Req Number"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Project Code</label>
                  <select
                    value={projectCode}
                    onChange={(e) => setProjectCode(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select Project Code</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Project Name</label>
                  <select
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select Project Name</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All Status</option>
                    <option value="Cancel">Cancel</option>
                    <option value="Close">Close</option>
                    <option value="Waiting">Waiting</option>
                    <option value="Approved">Approved</option>
                    <option value="DisApproved">DisApproved</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    value={store}
                    onChange={(e) => setStore(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All Stores</option>
                    {storeOptions.map((s) => (
                      <option key={s.fld_storeid} value={s.fld_storeid}>
                        {s.fld_storename}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={clearFilters}
                  className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  Clear Filters
                </button>

                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                >
                  <Search size={16} />
                  {loading ? "Searching..." : "Search Records"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Requisition List</h3>
            </div>
          </div>

          {results.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Req No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Requested For</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Employee</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Authorized Person</th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">Action</th>
                  </tr>
                </thead>

                <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                  {results.map((item, idx) => (
                    <tr
                      key={idx}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                            isDark
                              ? "bg-gray-700 text-gray-200"
                              : `bg-${baseColor}-100 text-${baseColor}-800`
                          }`}
                        >
                          {item.fld_purchaseRequestNo}
                        </span>
                      </td>
                      <td className="px-4 py-3">{item.fld_date}</td>
                      <td className="px-4 py-3">{item.fld_type}</td>
                      <td className="px-4 py-3">{item.fld_indentref}</td>
                      <td className="px-4 py-3">{item.dept}</td>
                      <td className="px-4 py-3">{item.employee}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusBadge(
                            item.status
                          )}`}
                        >
                          {item.status || status}
                        </span>
                      </td>
                      <td className="px-4 py-3">{item.fld_username}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-1.5 rounded-full bg-yellow-100 text-yellow-600 hover:bg-yellow-200 transition"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <ClipboardList size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No Records Found
              </h3>
              <p className="text-gray-500 text-center max-w-md text-sm">
                Try adjusting your search filters or add a new material requisition record.
              </p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default PurchaseStoreMaterialListing;